import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight, 
  MapPin, 
  Calendar,
  Wallet,
  Users,
  Sparkles,
  Compass,
  Zap,
  Loader2,
  Navigation,
  Mountain,
  Waves,
  Building2,
  Coffee,
  Tent,
  Heart,
  Check,
  Home,
  Sun,
  Moon,
  Plane,
  Camera,
  Music,
  Utensils,
  Palmtree,
  Clock,
  DollarSign,
  Search,
  TrendingUp,
  Globe,
  ChevronRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlanStore } from '../store/planStore';
import { useAuth } from '../contexts/AuthContext';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import api from '../services/api';
import { markPlanCreated } from '../components/LandingCarousel';
import PlanRevealModal from '../components/PlanRevealModal';

// Location Autocomplete Component using OpenStreetMap Nominatim
const LocationAutocomplete = ({ 
  value, 
  onChange, 
  placeholder, 
  className, 
  autoFocus,
  testId,
  icon: Icon = Search
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLocations = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // Use OpenStreetMap Nominatim API (free, no API key needed)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6&addressdetails=1&featuretype=city`,
        {
          headers: {
            'Accept-Language': 'en',
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Transform and deduplicate results
        const seen = new Set();
        const locations = data
          .filter(item => {
            // Focus on cities, towns, and notable places
            const type = item.type;
            return ['city', 'town', 'village', 'municipality', 'administrative'].includes(type) ||
                   item.class === 'place' ||
                   item.class === 'boundary';
          })
          .map(item => {
            const city = item.address?.city || item.address?.town || item.address?.village || 
                        item.address?.municipality || item.name;
            const state = item.address?.state;
            const country = item.address?.country;
            
            // Create a display name
            let displayName = city;
            if (state && state !== city) displayName += `, ${state}`;
            if (country) displayName += `, ${country}`;
            
            return {
              id: item.place_id,
              name: city,
              displayName: displayName,
              country: country,
              lat: item.lat,
              lon: item.lon
            };
          })
          .filter(loc => {
            // Deduplicate by name + country
            const key = `${loc.name}-${loc.country}`.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 5);
        
        setSuggestions(locations);
        setShowSuggestions(locations.length > 0);
      }
    } catch (error) {
      console.error('Location search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      searchLocations(newValue);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.name);
    onChange(suggestion.name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <Icon size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
        data-testid={testId}
      />
      {inputValue && (
        <button
          onClick={handleClear}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
          type="button"
        >
          <X size={18} className="text-gray-400" />
        </button>
      )}
      {isLoading && (
        <Loader2 size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-teal-500 animate-spin" />
      )}
      
      {/* Suggestions dropdown */}
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            ref={suggestionsRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 shadow-xl overflow-hidden z-50"
            style={{ borderRadius: '1rem' }}
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id || index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-teal-50 transition-colors text-left border-b border-gray-100 last:border-0"
                data-testid={`suggestion-${index}`}
              >
                <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0.75rem' }}>
                  <MapPin size={18} className="text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{suggestion.name}</p>
                  <p className="text-sm text-gray-500 truncate">{suggestion.country}</p>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Simpler Origin Autocomplete (inline style for the origin card)
const OriginAutocomplete = ({ value, onChange, placeholder, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLocations = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1&featuretype=city`,
        { headers: { 'Accept-Language': 'en' } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const seen = new Set();
        const locations = data
          .filter(item => ['city', 'town', 'village', 'municipality', 'administrative'].includes(item.type) || item.class === 'place')
          .map(item => {
            const city = item.address?.city || item.address?.town || item.address?.village || item.name;
            const country = item.address?.country;
            return { id: item.place_id, name: city, country };
          })
          .filter(loc => {
            const key = `${loc.name}-${loc.country}`.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 4);
        
        setSuggestions(locations);
        setShowSuggestions(locations.length > 0);
      }
    } catch (error) {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLocations(newValue), 300);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.name);
    onChange(suggestion.name);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="w-full bg-transparent text-gray-900 font-semibold text-lg outline-none"
          data-testid={testId}
        />
        {isLoading && <Loader2 size={16} className="text-teal-500 animate-spin flex-shrink-0" />}
      </div>
      
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            ref={suggestionsRef}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 shadow-xl overflow-hidden z-50"
            style={{ borderRadius: '0.75rem' }}
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id || index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-3 py-2 flex items-center gap-2 hover:bg-teal-50 transition-colors text-left border-b border-gray-100 last:border-0 text-sm"
              >
                <MapPin size={14} className="text-teal-500 flex-shrink-0" />
                <span className="font-medium text-gray-900">{suggestion.name}</span>
                <span className="text-gray-400 text-xs truncate">{suggestion.country}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ step }) => (
  <div className="flex items-center gap-2 mb-2">
    {[1, 2, 3].map((s, i) => (
      <React.Fragment key={s}>
        <motion.div
          initial={false}
          animate={{
            scale: s === step ? 1.1 : 1,
            backgroundColor: s <= step ? '#14b8a6' : '#e5e7eb'
          }}
          className={`w-10 h-10 flex items-center justify-center font-bold text-sm transition-colors ${
            s <= step ? 'text-white' : 'text-gray-400'
          }`}
          style={{ borderRadius: '50%' }}
        >
          {s < step ? <Check size={18} /> : s}
        </motion.div>
        {i < 2 && (
          <motion.div 
            initial={{ scaleX: 0 }}
            animate={{ scaleX: s < step ? 1 : 0 }}
            className="h-1 flex-1 bg-teal-500 origin-left"
            style={{ borderRadius: '9999px' }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Background decoration component
const FloatingOrb = ({ className, delay = 0, color = "teal" }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ 
      opacity: [0.3, 0.5, 0.3],
      scale: [0.8, 1, 0.8],
      y: [0, -20, 0]
    }}
    transition={{ 
      duration: 6,
      repeat: Infinity,
      delay,
      ease: "easeInOut"
    }}
    className={`absolute blur-3xl ${className}`}
    style={{
      background: color === "teal" 
        ? "radial-gradient(circle, rgba(20, 184, 166, 0.4) 0%, transparent 70%)"
        : color === "amber"
        ? "radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, transparent 70%)"
        : "radial-gradient(circle, rgba(244, 114, 182, 0.3) 0%, transparent 70%)",
      borderRadius: "50%"
    }}
  />
);

const TRAVEL_STYLES = [
  { value: 'relaxed', label: 'Relaxed', icon: Coffee, desc: 'Slow pace, minimal activities', color: 'teal', gradient: 'from-teal-400 to-cyan-400' },
  { value: 'adventure', label: 'Adventure', icon: Tent, desc: 'Thrills & outdoor activities', color: 'orange', gradient: 'from-orange-400 to-red-400' },
  { value: 'cultural', label: 'Cultural', icon: Building2, desc: 'History, museums, local life', color: 'amber', gradient: 'from-amber-400 to-yellow-400' },
  { value: 'beach', label: 'Beach', icon: Waves, desc: 'Sun, sand & water sports', color: 'cyan', gradient: 'from-cyan-400 to-blue-400' },
  { value: 'mountains', label: 'Mountains', icon: Mountain, desc: 'Trekking & scenic views', color: 'emerald', gradient: 'from-emerald-400 to-green-400' },
  { value: 'romantic', label: 'Romantic', icon: Heart, desc: 'Couples getaway', color: 'rose', gradient: 'from-rose-400 to-pink-400' },
];

const COMPANIONS = [
  { value: 'solo', label: 'Solo', emoji: '🧘', desc: 'Me, myself & I' },
  { value: 'couple', label: 'Couple', emoji: '💑', desc: 'Romantic duo' },
  { value: 'family', label: 'Family', emoji: '👨‍👩‍👧‍👦', desc: 'With kids' },
  { value: 'friends', label: 'Friends', emoji: '👯', desc: 'Squad goals' },
  { value: 'group', label: 'Group', emoji: '👥', desc: '5+ people' },
];

const POPULAR_DESTINATIONS = [
  { name: 'Bali', country: 'Indonesia', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=300&q=80', tag: 'Trending' },
  { name: 'Paris', country: 'France', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=300&q=80', tag: 'Classic' },
  { name: 'Tokyo', country: 'Japan', image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=300&q=80', tag: 'Popular' },
  { name: 'Goa', country: 'India', image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=300&q=80', tag: 'Beach' },
  { name: 'Santorini', country: 'Greece', image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=300&q=80', tag: 'Romantic' },
  { name: 'Dubai', country: 'UAE', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=300&q=80', tag: 'Luxury' },
];

const DURATION_PRESETS = [
  { value: '3', label: 'Weekend', desc: '3 days', icon: '⚡' },
  { value: '5', label: 'Short', desc: '5 days', icon: '🌤️' },
  { value: '7', label: 'Week', desc: '7 days', icon: '🌴' },
  { value: '14', label: 'Extended', desc: '2 weeks', icon: '🏝️' },
];

// Budget presets based on currency (per person)
const getBudgetPresets = (currency) => {
  if (currency === 'INR') {
    return [
      { value: 'budget', label: 'Budget', min: 5000, max: 15000, desc: 'Backpacker style' },
      { value: 'mid', label: 'Mid-Range', min: 15000, max: 50000, desc: 'Comfortable travel' },
      { value: 'luxury', label: 'Luxury', min: 50000, max: 200000, desc: 'Premium experience' },
    ];
  } else if (currency === 'EUR') {
    return [
      { value: 'budget', label: 'Budget', min: 50, max: 200, desc: 'Backpacker style' },
      { value: 'mid', label: 'Mid-Range', min: 200, max: 800, desc: 'Comfortable travel' },
      { value: 'luxury', label: 'Luxury', min: 800, max: 3000, desc: 'Premium experience' },
    ];
  } else if (currency === 'GBP') {
    return [
      { value: 'budget', label: 'Budget', min: 40, max: 150, desc: 'Backpacker style' },
      { value: 'mid', label: 'Mid-Range', min: 150, max: 600, desc: 'Comfortable travel' },
      { value: 'luxury', label: 'Luxury', min: 600, max: 2500, desc: 'Premium experience' },
    ];
  }
  // Default USD
  return [
    { value: 'budget', label: 'Budget', min: 50, max: 200, desc: 'Backpacker style' },
    { value: 'mid', label: 'Mid-Range', min: 200, max: 1000, desc: 'Comfortable travel' },
    { value: 'luxury', label: 'Luxury', min: 1000, max: 5000, desc: 'Premium experience' },
  ];
};

export default function Planning() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCurrentPlan, setIsGenerating, setError, currentPlan } = usePlanStore();
  const { user } = useAuth();
  const { currency, currencySymbol, formatCurrency } = useLocationContext();
  
  const mode = searchParams.get('mode') || 'plan';
  const prefilledDestination = searchParams.get('destination') || '';
  const eventName = searchParams.get('event') || '';
  const fromCity = searchParams.get('from') || '';
  
  const [step, setStep] = useState(1);
  const [destination, setDestination] = useState(prefilledDestination);
  const [origin, setOrigin] = useState(fromCity);
  const [duration, setDuration] = useState('');
  const [budgetPreset, setBudgetPreset] = useState(null);
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [travelStyle, setTravelStyle] = useState(null);
  const [companions, setCompanions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(!fromCity);
  const [showCustomBudget, setShowCustomBudget] = useState(false);
  const [destinationEvents, setDestinationEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showPlanReveal, setShowPlanReveal] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [iplMatches, setIplMatches] = useState([]);
  const [showIplNudge, setShowIplNudge] = useState(true);
  
  // Invalid server-side locations that shouldn't be used (data center locations)
  const invalidLocations = ['council bluffs', 'ashburn', 'boardman', 'the dalles'];
  
  // Detect location if not provided
  useEffect(() => {
    if (!fromCity && navigator.geolocation) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
            );
            const data = await response.json();
            const detectedCity = data.city || data.locality || '';
            
            // Filter out invalid server-side locations
            if (detectedCity && !invalidLocations.includes(detectedCity.toLowerCase())) {
              setOrigin(detectedCity);
            } else {
              // Leave empty so user enters manually
              setOrigin('');
            }
          } catch {
            setOrigin('');
          } finally {
            setIsLocating(false);
          }
        },
        () => {
          // Geolocation denied - leave empty
          setOrigin('');
          setIsLocating(false);
        },
        { timeout: 5000 }
      );
    }
  }, [fromCity]);
  
  // Fetch events when destination changes - using AI-powered events
  useEffect(() => {
    const fetchDestinationEvents = async () => {
      if (!destination || destination.length < 3) {
        setDestinationEvents([]);
        return;
      }
      
      setEventsLoading(true);
      try {
        // Try AI-powered events first (Gemini-based)
        const aiData = await api.getAIEventsByDestination(destination, 4);
        if (aiData.success && aiData.events && aiData.events.length > 0) {
          setDestinationEvents(aiData.events.slice(0, 4));
        } else {
          // Fallback to regular search
          const data = await api.searchEvents({ city: destination, limit: 4 });
          if (data.events && data.events.length > 0) {
            setDestinationEvents(data.events.slice(0, 4));
          } else {
            setDestinationEvents([]);
          }
        }
      } catch (error) {
        console.error('Error fetching destination events:', error);
        // Try fallback on error
        try {
          const data = await api.searchEvents({ city: destination, limit: 4 });
          if (data.events && data.events.length > 0) {
            setDestinationEvents(data.events.slice(0, 4));
          } else {
            setDestinationEvents([]);
          }
        } catch {
          setDestinationEvents([]);
        }
      } finally {
        setEventsLoading(false);
      }
    };
    
    // Debounce the API call
    const timer = setTimeout(fetchDestinationEvents, 500);
    return () => clearTimeout(timer);
  }, [destination]);

  // Fetch IPL matches when destination changes
  useEffect(() => {
    const fetchIPLMatches = async () => {
      if (!destination || destination.length < 3) {
        setIplMatches([]);
        return;
      }
      
      try {
        const data = await api.getIPLEventsByCity(destination, 5);
        if (data.success && data.events && data.events.length > 0) {
          setIplMatches(data.events);
          setShowIplNudge(true);
        } else {
          setIplMatches([]);
        }
      } catch (error) {
        console.error('Error fetching IPL matches:', error);
        setIplMatches([]);
      }
    };
    
    const timer = setTimeout(fetchIPLMatches, 600);
    return () => clearTimeout(timer);
  }, [destination]);
  
  const handleGeneratePlan = async () => {
    try {
      setLoading(true);
      setIsGenerating(true);
      setError(null);
      
      // Require origin for trip planning
      if (!origin || origin.trim() === '') {
        toast.error('Please enter your departure city');
        setLoading(false);
        setIsGenerating(false);
        return;
      }
      
      const input = { 
        origin: origin,
        currency: currency || 'INR'
      };
      
      if (destination) input.destination = destination;
      if (duration) input.duration_days = parseInt(duration);
      if (budgetMin) input.budget_min = parseFloat(budgetMin);
      if (budgetMax) input.budget_max = parseFloat(budgetMax);
      if (travelStyle) input.travel_style = travelStyle;
      if (companions) input.companions = companions;
      
      // Check for IPL match context
      const iplContext = sessionStorage.getItem('iplMatchContext');
      if (iplContext) {
        try {
          const match = JSON.parse(iplContext);
          input.special_event = {
            type: 'ipl_match',
            name: match.name,
            date: match.date,
            time: match.time,
            venue: match.venue
          };
          input.user_intent = `IMPORTANT: Include the IPL cricket match "${match.name}" on ${match.formatted_date || match.date} at ${match.time} at ${match.venue || match.city} as a KEY ACTIVITY in the itinerary. Plan activities around this match - morning sightseeing, evening at the stadium. Make this the highlight of the trip!`;
          // Clear the context after using it
          sessionStorage.removeItem('iplMatchContext');
        } catch (e) {
          console.error('Error parsing IPL context:', e);
        }
      }
      
      if (mode === 'discover') {
        input.user_intent = "I'm exploring options. Suggest something amazing!";
      } else if (mode === 'surprise') {
        // Intelligent surprise mode based on location and season
        const month = new Date().getMonth();
        const season = month >= 2 && month <= 5 ? 'summer' : month >= 6 && month <= 9 ? 'monsoon' : 'winter';
        
        // Build intelligent prompt based on user's location
        input.user_intent = `Surprise me with an amazing destination! I'm currently in ${origin || 'India'}. 
Consider: 
- Current season (${season}) and weather suitability
- Popular destinations accessible from my location
- Any upcoming festivals or events in the next month
- A mix of adventure, culture, and relaxation
Don't suggest Udaipur - give me something unexpected and exciting!`;
      }
      
      const response = await api.generatePlan(input);
      
      if (response.success) {
        setCurrentPlan(response.plan);
        setGeneratedPlan(response.plan);
        markPlanCreated(); // Mark that user has created a plan
        setShowPlanReveal(true); // Show the beautiful plan reveal modal
        toast.success('Your plan is ready! ✨');
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message || 'Failed to generate plan. Please try again.');
    } finally {
      setLoading(false);
      setIsGenerating(false);
    }
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return destination.trim().length > 0 || mode === 'discover';
      default: return true;
    }
  };
  
  const totalSteps = 3;
  
  // Surprise Mode
  if (mode === 'surprise') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-6 relative overflow-hidden">
        {/* Plan Reveal Modal for Surprise Mode */}
        <PlanRevealModal 
          plan={generatedPlan}
          isOpen={showPlanReveal}
          onClose={() => setShowPlanReveal(false)}
          formatCurrency={(amount) => {
            if (!amount) return `${currencySymbol}0`;
            return `${currencySymbol}${amount.toLocaleString()}`;
          }}
        />
        
        {/* Animated background */}
        <FloatingOrb className="w-96 h-96 top-0 -left-48" color="amber" delay={0} />
        <FloatingOrb className="w-72 h-72 bottom-0 -right-36" color="teal" delay={1} />
        <FloatingOrb className="w-48 h-48 top-1/3 right-1/4" color="pink" delay={2} />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center relative z-10"
        >
          <motion.div
            animate={{ 
              rotate: [0, 5, -5, 0], 
              scale: [1, 1.05, 1],
              y: [0, -5, 0]
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex items-center justify-center w-28 h-28 mb-8 bg-gradient-to-br from-amber-200 to-orange-200 shadow-2xl shadow-amber-300/50"
            style={{ borderRadius: '2rem' }}
          >
            <Sparkles size={56} className="text-amber-600" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-black text-gray-900 mb-4"
          >
            Ready for <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">Magic?</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-gray-600 text-lg mb-6 max-w-sm mx-auto"
          >
            Our AI will craft an unforgettable adventure based on your Travel DNA and current trends.
          </motion.p>
          
          {/* Origin Input for Surprise Mode */}
          {!loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-6 text-left"
            >
              <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                Where are you traveling from?
              </label>
              <div className="relative">
                <Navigation size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-500" />
                <input
                  type="text"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder={isLocating ? "Detecting your location..." : "Enter your city"}
                  className="w-full pl-12 pr-4 py-4 bg-white border-2 border-amber-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:border-amber-400 focus:outline-none transition-colors"
                  data-testid="surprise-origin-input"
                />
                {isLocating && (
                  <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-amber-500" />
                )}
              </div>
              {origin && (
                <p className="text-xs text-amber-600 mt-2 text-center">
                  Departing from {origin}
                </p>
              )}
            </motion.div>
          )}
          
          {loading ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-4"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center shadow-lg" style={{ borderRadius: '1.5rem' }}>
                <Loader2 size={40} className="animate-spin text-amber-500" />
              </div>
              <p className="text-gray-500 font-medium">Creating magic...</p>
              <p className="text-gray-400 text-sm">This takes about 20-30 seconds</p>
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4"
            >
              <button
                onClick={handleGeneratePlan}
                className="w-full py-5 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white font-bold text-lg shadow-2xl shadow-orange-500/40 hover:shadow-orange-500/60 hover:scale-[1.02] transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="surprise-generate-btn"
              >
                <span className="flex items-center justify-center gap-3">
                  <Sparkles size={24} />
                  Surprise Me!
                </span>
              </button>
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-amber-600 transition-colors font-medium"
              >
                ← Back to home
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 relative overflow-hidden">
      {/* Plan Reveal Modal */}
      <PlanRevealModal 
        plan={generatedPlan}
        isOpen={showPlanReveal}
        onClose={() => setShowPlanReveal(false)}
        formatCurrency={(amount) => {
          if (!amount) return `${currencySymbol}0`;
          return `${currencySymbol}${amount.toLocaleString()}`;
        }}
      />
      
      {/* Background decorations */}
      <div className="fixed inset-0 pointer-events-none">
        <FloatingOrb className="w-96 h-96 -top-48 -left-48" color="teal" delay={0} />
        <FloatingOrb className="w-72 h-72 top-1/3 -right-36" color="amber" delay={1.5} />
        <FloatingOrb className="w-48 h-48 bottom-1/4 left-1/4" color="pink" delay={3} />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-gray-100/50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
            className="flex items-center gap-2 px-4 py-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 transition-all"
            style={{ borderRadius: '9999px' }}
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline font-medium">Back</span>
          </button>
          
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <PanchiBirdLogo className="w-10 h-10" />
            <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent hidden sm:block">Panchi</span>
          </Link>
          
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span className="hidden sm:inline">Step {step} of {totalSteps}</span>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-2 transition-all duration-500 ${
                    s <= step 
                      ? 'bg-gradient-to-r from-teal-400 to-cyan-500 w-8' 
                      : 'bg-gray-200 w-2'
                  }`}
                  style={{ borderRadius: '9999px' }}
                />
              ))}
            </div>
          </div>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12 relative z-10">
        <AnimatePresence mode="wait">
          {/* Step 1: Where To */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 text-sm font-bold mb-4"
                  style={{ borderRadius: '9999px' }}
                >
                  <MapPin size={16} />
                  Step 1 • Destination
                </motion.div>
                <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
                  {mode === 'discover' ? 'Where shall we explore?' : eventName ? 'Plan Your Event Trip' : 'Where to next?'}
                </h1>
                <p className="text-gray-500 text-lg max-w-md mx-auto">
                  {mode === 'discover' ? 'Enter a destination or let our AI surprise you' : eventName ? `Planning a trip for ${eventName}` : 'Tell us your dream destination'}
                </p>
              </div>
              
              {/* Event Context Banner */}
              {eventName && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 mb-6 flex items-center gap-4"
                  style={{ borderRadius: '1rem' }}
                >
                  <div className="w-12 h-12 bg-rose-100 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0.75rem' }}>
                    <Calendar size={24} className="text-rose-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">{eventName}</p>
                    <p className="text-sm text-gray-600">in {prefilledDestination}</p>
                  </div>
                  <span className="px-3 py-1 bg-rose-500 text-white text-xs font-bold" style={{ borderRadius: '9999px' }}>
                    Event Trip
                  </span>
                </motion.div>
              )}
              
              {/* Origin Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-5 bg-white/80 backdrop-blur-xl border border-gray-100 shadow-lg" 
                style={{ borderRadius: '1.25rem' }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '1rem' }}>
                    {isLocating ? (
                      <Loader2 size={22} className="text-teal-500 animate-spin" />
                    ) : (
                      <Navigation size={22} className="text-teal-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-400 text-sm font-medium">Departing from</span>
                    <OriginAutocomplete
                      value={origin}
                      onChange={setOrigin}
                      placeholder="Your city"
                      testId="origin-input"
                    />
                  </div>
                </div>
              </motion.div>
              
              {/* Destination Input - Hero Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-8 md:p-10 bg-white/90 backdrop-blur-xl border border-gray-100 shadow-2xl" 
                style={{ borderRadius: '2rem' }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/30" style={{ borderRadius: '1rem' }}>
                    <Plane size={28} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Destination</h3>
                    <p className="text-gray-400 text-sm">Where dreams come true</p>
                  </div>
                </div>
                
                <div className="relative">
                  <LocationAutocomplete
                    value={destination}
                    onChange={setDestination}
                    placeholder="Paris, Tokyo, Bali..."
                    className="w-full pl-14 pr-12 py-5 bg-gray-50 border-2 border-gray-100 text-2xl md:text-3xl font-bold text-gray-900 placeholder-gray-300 focus:border-teal-400 focus:bg-white outline-none transition-all"
                    style={{ borderRadius: '1rem' }}
                    autoFocus
                    testId="destination-input"
                    icon={Search}
                  />
                </div>
              </motion.div>
              
              {/* Popular Destinations */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={18} className="text-teal-500" />
                  <span className="text-sm font-bold text-gray-600 uppercase tracking-wider">Popular Picks</span>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {POPULAR_DESTINATIONS.map((dest, index) => (
                    <motion.button
                      key={dest.name}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                      onClick={() => setDestination(dest.name)}
                      className={`group relative overflow-hidden aspect-[4/5] ${
                        destination === dest.name ? 'ring-2 ring-teal-500 ring-offset-2' : ''
                      }`}
                      style={{ borderRadius: '1rem' }}
                    >
                      <img 
                        src={dest.image} 
                        alt={dest.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <span className="absolute top-2 right-2 px-2 py-0.5 bg-white/90 text-[10px] font-bold text-gray-700" style={{ borderRadius: '9999px' }}>
                        {dest.tag}
                      </span>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h4 className="text-white font-bold text-sm">{dest.name}</h4>
                        <p className="text-white/70 text-xs">{dest.country}</p>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
              
              {/* IPL 2026 Match Nudge */}
              <AnimatePresence>
                {iplMatches.length > 0 && showIplNudge && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div 
                      className="relative p-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white shadow-lg overflow-hidden"
                      style={{ borderRadius: '1.25rem' }}
                      data-testid="ipl-nudge-banner"
                    >
                      {/* Cricket ball pattern background */}
                      <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                        <svg viewBox="0 0 100 100" className="w-full h-full">
                          <circle cx="50" cy="50" r="45" fill="white"/>
                          <path d="M50 5 Q80 50 50 95" stroke="red" strokeWidth="3" fill="none"/>
                          <path d="M50 5 Q20 50 50 95" stroke="red" strokeWidth="3" fill="none"/>
                        </svg>
                      </div>
                      
                      <div className="relative flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">🏏</span>
                            <span className="font-black text-lg">IPL 2026 Alert!</span>
                          </div>
                          
                          <p className="text-white/90 text-sm mb-3">
                            {iplMatches.length} IPL {iplMatches.length === 1 ? 'match' : 'matches'} in {destination}! Plan your trip around cricket fever.
                          </p>
                          
                          {/* Show first 2 matches */}
                          <div className="space-y-2">
                            {iplMatches.slice(0, 2).map((match, idx) => (
                              <div 
                                key={match.id || idx}
                                className="flex items-center gap-2 p-2 bg-white/20 backdrop-blur-sm"
                                style={{ borderRadius: '0.5rem' }}
                              >
                                <div className="w-8 h-8 bg-white/30 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '50%' }}>
                                  <span className="text-sm">🎯</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-sm truncate">{match.short_name || match.name}</p>
                                  <p className="text-white/80 text-xs">{match.formatted_date} • {match.time}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {iplMatches.length > 2 && (
                            <p className="text-white/70 text-xs mt-2">
                              +{iplMatches.length - 2} more {iplMatches.length - 2 === 1 ? 'match' : 'matches'}
                            </p>
                          )}
                        </div>
                        
                        <button
                          onClick={() => setShowIplNudge(false)}
                          className="p-1 hover:bg-white/20 transition-colors flex-shrink-0"
                          style={{ borderRadius: '50%' }}
                          aria-label="Dismiss"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      
                      <button
                        onClick={() => {
                          const firstMatch = iplMatches[0];
                          if (firstMatch && firstMatch.date) {
                            // Set duration to 3 days (perfect for a match weekend)
                            setDuration('3');
                            
                            // Store the IPL match info for inclusion in itinerary
                            const matchContext = {
                              name: firstMatch.name || firstMatch.short_name,
                              date: firstMatch.date,
                              formatted_date: firstMatch.formatted_date,
                              time: firstMatch.time || '19:30',
                              venue: firstMatch.venue,
                              city: firstMatch.city
                            };
                            sessionStorage.setItem('iplMatchContext', JSON.stringify(matchContext));
                            
                            toast.success(`Trip set for ${firstMatch.short_name || firstMatch.name}!`, {
                              description: `3-day trip including match on ${firstMatch.formatted_date}`,
                            });
                            
                            // Scroll to duration section and auto-select
                            setTimeout(() => {
                              document.querySelector('[data-testid="duration-preset-3"]')?.scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'center' 
                              });
                            }, 300);
                          }
                        }}
                        className="mt-3 w-full py-2.5 bg-white text-purple-700 font-bold text-sm hover:bg-white/90 transition-all flex items-center justify-center gap-2"
                        style={{ borderRadius: '9999px' }}
                        data-testid="plan-ipl-trip-btn"
                      >
                        <Plane size={16} />
                        Plan Trip Around IPL Match
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Trending Events at Destination */}
              <AnimatePresence>
                {destination && destination.length >= 3 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100" style={{ borderRadius: '1.25rem' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.5rem' }}>
                          <TrendingUp size={16} className="text-amber-600" />
                        </div>
                        <span className="font-bold text-gray-800">Events at {destination}</span>
                        {eventsLoading && <Loader2 size={16} className="text-amber-500 animate-spin ml-auto" />}
                      </div>
                      
                      {eventsLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 size={24} className="text-amber-500 animate-spin" />
                        </div>
                      ) : destinationEvents.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {destinationEvents.map((event, index) => (
                            <motion.div
                              key={event.id || index}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex flex-col p-3 bg-white/80 backdrop-blur border border-amber-100/50 hover:border-amber-300 hover:shadow-md transition-all group"
                              style={{ borderRadius: '0.75rem' }}
                              data-testid={`destination-event-${index}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
                                  event.category === 'concert' || event.category === 'music' ? 'bg-purple-100' :
                                  event.category === 'sports' ? 'bg-emerald-100' :
                                  event.category === 'festival' ? 'bg-pink-100' : 'bg-blue-100'
                                }`} style={{ borderRadius: '0.5rem' }}>
                                  {event.category === 'concert' || event.category === 'music' ? <Music size={18} className="text-purple-600" /> :
                                   event.category === 'sports' ? <Zap size={18} className="text-emerald-600" /> :
                                   event.category === 'festival' ? <Sparkles size={18} className="text-pink-600" /> :
                                   <Globe size={18} className="text-blue-600" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 text-sm truncate">{event.name}</p>
                                  <p className="text-xs text-gray-500 flex items-center gap-1">
                                    <Calendar size={10} />
                                    {event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Upcoming'}
                                    {event.city && (
                                      <>
                                        <span className="mx-1">•</span>
                                        <MapPin size={10} />
                                        {event.city}
                                      </>
                                    )}
                                  </p>
                                </div>
                                {event.days_until !== undefined && event.days_until <= 30 && (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold flex-shrink-0" style={{ borderRadius: '9999px' }}>
                                    {event.days_until <= 7 ? 'This Week!' : 'Soon'}
                                  </span>
                                )}
                              </div>
                              {/* Plan Trip Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Set the destination and trigger a plan with event context
                                  setDestination(event.city || destination);
                                  if (event.date) {
                                    const eventDate = new Date(event.date);
                                    const formattedDate = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                    toast.success(`Planning trip around ${event.name}`, {
                                      description: `Event date: ${formattedDate}`,
                                    });
                                  }
                                  // Scroll to generate button
                                  document.querySelector('[data-testid="generate-plan-btn"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }}
                                className="mt-2 w-full py-1.5 px-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-semibold flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all"
                                style={{ borderRadius: '0.5rem' }}
                                data-testid={`plan-trip-event-${index}`}
                              >
                                <Plane size={12} />
                                Plan Trip Around This
                              </button>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 text-sm py-4">
                          No upcoming events found at {destination}. Try another destination!
                        </p>
                      )}
                      
                      {destinationEvents.length > 0 && (
                        <p className="text-xs text-amber-600 mt-3 text-center">
                          Time your trip around these events for an amazing experience!
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Smart Trip Timing Suggestion */}
              <AnimatePresence>
                {(destinationEvents.length > 0 || iplMatches.length > 0) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100" style={{ borderRadius: '1rem' }}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 text-white" style={{ borderRadius: '0.75rem' }}>
                          <Sparkles size={18} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 text-sm mb-1">Smart Trip Timing</h4>
                          
                          {/* IPL Match Info - Priority */}
                          {iplMatches.length > 0 && iplMatches[0] && (
                            <p className="text-xs text-gray-600 mb-1">
                              <span className="text-purple-600 font-semibold">🏏 {iplMatches[0].short_name || iplMatches[0].name}</span> on {iplMatches[0].formatted_date}. Cricket + Travel = Perfect combo!
                            </p>
                          )}
                          
                          {/* Other Events */}
                          {destinationEvents[0] && (
                            <p className="text-xs text-gray-600">
                              <span className="text-purple-600 font-semibold">{destinationEvents[0].name}</span> is happening 
                              {destinationEvents[0].days_until !== undefined && (
                                destinationEvents[0].days_until <= 7 
                                  ? ' this week!' 
                                  : destinationEvents[0].days_until <= 30 
                                    ? ` in ${destinationEvents[0].days_until} days.`
                                    : ` on ${destinationEvents[0].date ? new Date(destinationEvents[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'soon'}.`
                              )} Plan your trip to catch it!
                            </p>
                          )}
                          
                          {/* Event counts */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {iplMatches.length > 0 && (
                              <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 font-medium" style={{ borderRadius: '9999px' }}>
                                🏏 {iplMatches.length} IPL match{iplMatches.length > 1 ? 'es' : ''}
                              </span>
                            )}
                            {destinationEvents.length > 0 && (
                              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 font-medium" style={{ borderRadius: '9999px' }}>
                                +{destinationEvents.length} event{destinationEvents.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Action Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-3 pt-4"
              >
                {mode === 'discover' && (
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 flex items-center justify-center gap-2 py-4 bg-white border-2 border-gray-200 text-gray-700 font-bold hover:border-teal-300 hover:bg-teal-50 transition-all"
                    style={{ borderRadius: '9999px' }}
                    data-testid="skip-destination-btn"
                  >
                    <Compass size={20} />
                    Let AI Decide
                  </button>
                )}
                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceed()}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg shadow-xl transition-all ${!canProceed() ? 'opacity-50 cursor-not-allowed' : 'shadow-teal-500/40 hover:shadow-teal-500/60 hover:scale-[1.02]'}`}
                  style={{ borderRadius: '9999px' }}
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight size={20} />
                </button>
              </motion.div>
            </motion.div>
          )}
          
          {/* Step 2: Trip Details */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 text-sm font-bold mb-4"
                  style={{ borderRadius: '9999px' }}
                >
                  <Calendar size={16} />
                  Step 2 • Details
                </motion.div>
                <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
                  Plan your trip
                </h1>
                <p className="text-gray-500 text-lg max-w-md mx-auto">
                  {destination ? `Planning ${destination} getaway` : 'How long and what budget?'}
                </p>
              </div>
              
              {/* Duration Selection */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-6 md:p-8 bg-white/90 backdrop-blur-xl border border-gray-100 shadow-xl"
                style={{ borderRadius: '1.5rem' }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center" style={{ borderRadius: '1rem' }}>
                    <Clock size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Trip Duration</h3>
                    <p className="text-gray-400 text-sm">How many days?</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {DURATION_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setDuration(preset.value)}
                      className={`p-4 border-2 text-center transition-all ${
                        duration === preset.value
                          ? 'border-amber-400 bg-amber-50 shadow-md'
                          : 'border-gray-100 bg-white hover:border-gray-200'
                      }`}
                      style={{ borderRadius: '1rem' }}
                      data-testid={`duration-${preset.value}`}
                    >
                      <span className="text-2xl mb-2 block">{preset.icon}</span>
                      <span className="font-bold text-gray-900 block">{preset.label}</span>
                      <span className="text-gray-400 text-sm">{preset.desc}</span>
                    </button>
                  ))}
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 text-sm">Or enter custom:</span>
                  <input
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    placeholder="Days"
                    className="w-24 px-4 py-2 bg-gray-50 border-2 border-gray-200 text-gray-900 font-bold text-center focus:border-amber-400 outline-none transition-all"
                    style={{ borderRadius: '9999px' }}
                    min="1"
                    max="30"
                    data-testid="duration-input"
                  />
                </div>
              </motion.div>
              
              {/* Budget Selection */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 md:p-8 bg-white/90 backdrop-blur-xl border border-gray-100 shadow-xl"
                style={{ borderRadius: '1.5rem' }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center" style={{ borderRadius: '1rem' }}>
                    <Wallet size={24} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Budget Range</h3>
                    <p className="text-gray-400 text-sm">Per person in {currency}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  {getBudgetPresets(currency).map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => {
                        setBudgetPreset(preset.value);
                        setBudgetMin(preset.min.toString());
                        setBudgetMax(preset.max.toString());
                        setShowCustomBudget(false);
                      }}
                      className={`p-5 border-2 text-left transition-all ${
                        budgetPreset === preset.value
                          ? 'border-emerald-400 bg-emerald-50 shadow-md'
                          : 'border-gray-100 bg-white hover:border-gray-200'
                      }`}
                      style={{ borderRadius: '1rem' }}
                      data-testid={`budget-${preset.value}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {[...Array(preset.value === 'budget' ? 1 : preset.value === 'mid' ? 2 : 3)].map((_, i) => (
                          <DollarSign key={i} size={18} className={budgetPreset === preset.value ? 'text-emerald-600' : 'text-gray-400'} />
                        ))}
                      </div>
                      <span className="font-bold text-gray-900 block text-lg">{preset.label}</span>
                      <span className="text-gray-400 text-sm">{preset.desc}</span>
                      <span className="text-gray-500 text-xs block mt-1">{currencySymbol}{preset.min} - {currencySymbol}{preset.max}</span>
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => setShowCustomBudget(!showCustomBudget)}
                  className="text-sm text-teal-600 font-medium hover:text-teal-700"
                >
                  {showCustomBudget ? 'Hide custom budget' : 'Enter custom budget →'}
                </button>
                
                {showCustomBudget && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex gap-3 mt-4"
                  >
                    <input
                      type="number"
                      value={budgetMin}
                      onChange={(e) => { setBudgetMin(e.target.value); setBudgetPreset(null); }}
                      placeholder="Min"
                      className="flex-1 px-4 py-3 bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500 outline-none transition-all"
                      style={{ borderRadius: '9999px' }}
                      data-testid="budget-min-input"
                    />
                    <span className="flex items-center text-gray-400">to</span>
                    <input
                      type="number"
                      value={budgetMax}
                      onChange={(e) => { setBudgetMax(e.target.value); setBudgetPreset(null); }}
                      placeholder="Max"
                      className="flex-1 px-4 py-3 bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500 outline-none transition-all"
                      style={{ borderRadius: '9999px' }}
                      data-testid="budget-max-input"
                    />
                  </motion.div>
                )}
              </motion.div>
              
              {/* Action Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex gap-3 pt-4"
              >
                <button
                  onClick={() => setStep(3)}
                  className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 font-bold hover:border-teal-300 transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="skip-details-btn"
                >
                  Skip
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg shadow-xl shadow-teal-500/40 hover:shadow-teal-500/60 hover:scale-[1.02] transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight size={20} />
                </button>
              </motion.div>
            </motion.div>
          )}
          
          {/* Step 3: Travel Style & Companions */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 text-rose-700 text-sm font-bold mb-4"
                  style={{ borderRadius: '9999px' }}
                >
                  <Heart size={16} />
                  Step 3 • Personalize
                </motion.div>
                <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
                  Your travel vibe
                </h1>
                <p className="text-gray-500 text-lg max-w-md mx-auto">
                  Help us personalize your perfect trip
                </p>
              </div>
              
              {/* Travel Style */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Compass size={20} className="text-teal-500" />
                  How do you like to travel?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {TRAVEL_STYLES.map((style, index) => {
                    const Icon = style.icon;
                    const isSelected = travelStyle === style.value;
                    return (
                      <motion.button
                        key={style.value}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                        onClick={() => setTravelStyle(isSelected ? null : style.value)}
                        className={`relative p-5 bg-white border-2 text-left transition-all overflow-hidden group ${
                          isSelected ? 'border-teal-400 shadow-xl' : 'border-gray-100 hover:border-gray-200 hover:shadow-md'
                        }`}
                        style={{ borderRadius: '1.25rem' }}
                        data-testid={`style-${style.value}`}
                      >
                        {/* Gradient background on select */}
                        {isSelected && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.1 }}
                            className={`absolute inset-0 bg-gradient-to-br ${style.gradient}`}
                          />
                        )}
                        
                        <div className={`relative z-10 w-12 h-12 flex items-center justify-center mb-3 transition-colors ${
                          isSelected 
                            ? `bg-gradient-to-br ${style.gradient} shadow-lg` 
                            : 'bg-gray-100 group-hover:bg-gray-200'
                        }`} style={{ borderRadius: '1rem' }}>
                          <Icon size={24} className={isSelected ? 'text-white' : 'text-gray-500'} />
                        </div>
                        <span className={`relative z-10 block font-bold text-lg ${isSelected ? 'text-teal-700' : 'text-gray-900'}`}>
                          {style.label}
                        </span>
                        <span className="relative z-10 text-gray-500 text-sm">{style.desc}</span>
                        
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-3 right-3 w-6 h-6 bg-teal-500 flex items-center justify-center shadow-lg"
                            style={{ borderRadius: '50%' }}
                          >
                            <Check size={14} className="text-white" />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
              
              {/* Companions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users size={20} className="text-rose-500" />
                  {"Who's joining you?"}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {COMPANIONS.map((comp, index) => {
                    const isSelected = companions === comp.value;
                    return (
                      <motion.button
                        key={comp.value}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + index * 0.05 }}
                        onClick={() => setCompanions(isSelected ? null : comp.value)}
                        className={`flex items-center gap-3 px-5 py-4 border-2 font-medium transition-all ${
                          isSelected 
                            ? 'bg-gradient-to-r from-teal-500 to-cyan-500 border-transparent text-white shadow-xl shadow-teal-500/30' 
                            : 'bg-white border-gray-200 text-gray-700 hover:border-teal-300 hover:shadow-md'
                        }`}
                        style={{ borderRadius: '9999px' }}
                        data-testid={`companion-${comp.value}`}
                      >
                        <span className="text-2xl">{comp.emoji}</span>
                        <div className="text-left">
                          <span className="block font-bold">{comp.label}</span>
                          <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>{comp.desc}</span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
              
              {/* Summary Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100"
                style={{ borderRadius: '1.5rem' }}
              >
                <h4 className="font-bold text-teal-800 mb-3 flex items-center gap-2">
                  <Sparkles size={18} />
                  Your Trip Summary
                </h4>
                <div className="flex flex-wrap gap-2">
                  {destination && (
                    <span className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium" style={{ borderRadius: '9999px' }}>
                      📍 {destination}
                    </span>
                  )}
                  {origin && (
                    <span className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium" style={{ borderRadius: '9999px' }}>
                      🛫 From {origin}
                    </span>
                  )}
                  {duration && (
                    <span className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium" style={{ borderRadius: '9999px' }}>
                      📅 {duration} days
                    </span>
                  )}
                  {budgetPreset && (
                    <span className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium" style={{ borderRadius: '9999px' }}>
                      💰 {budgetPreset.charAt(0).toUpperCase() + budgetPreset.slice(1)} budget
                    </span>
                  )}
                  {travelStyle && (
                    <span className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium" style={{ borderRadius: '9999px' }}>
                      ✨ {travelStyle.charAt(0).toUpperCase() + travelStyle.slice(1)} style
                    </span>
                  )}
                  {companions && (
                    <span className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium" style={{ borderRadius: '9999px' }}>
                      👥 {companions.charAt(0).toUpperCase() + companions.slice(1)}
                    </span>
                  )}
                </div>
              </motion.div>
              
              {/* Generate Button */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={handleGeneratePlan}
                disabled={loading}
                className="w-full py-6 bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500 bg-[length:200%_auto] text-white font-bold text-xl shadow-2xl shadow-teal-500/40 hover:shadow-teal-500/60 hover:scale-[1.02] hover:bg-right transition-all disabled:opacity-50 disabled:hover:scale-100"
                style={{ borderRadius: '9999px' }}
                data-testid="generate-plan-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <Loader2 size={28} className="animate-spin" />
                    Creating your perfect trip...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    <Zap size={28} />
                    Generate My Plan
                    <ArrowRight size={24} />
                  </span>
                )}
              </motion.button>
              
              {loading && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center text-gray-400"
                >
                  Our AI is crafting a personalized itinerary just for you. This usually takes 20-30 seconds...
                </motion.p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
